import { apiClient, handleApiResponse } from './client'
import type { LayeredAnalytics, ApiResponse } from '@/types'

export interface StoreInfo {
  id?: string | number
  name?: string | { [locale: string]: string }
  /**
   * The *.mitiendanube.com host. NOT where a shopper's browser ends up: a store
   * with a custom domain redirects away from it. Use `url_with_protocol` for
   * anything that opens the storefront — see resolveStorefront().
   */
  domain?: string
  /** Canonical storefront URL, already carrying its protocol. */
  url_with_protocol?: string
  /** Every domain the store answers on (may be http:// and may include www). */
  domains?: string[]
  original_domain?: string
  country?: string
  email?: string
  /** Roadmap R1 P2 — written by the hourly aggregation pass; null until the first pass after connect. */
  lifecycle?: StoreLifecycle | null
  /** Roadmap R1 P3 — the two-speed store baseline set; null until the first lock. */
  baseline_set?: StoreBaselineSet | null
  /** Roadmap R1 — theme family + asset hash, refreshed daily. */
  theme?: { family: string | null; asset_hash: string | null; confidence: 'high' | 'low' | 'none'; detected_at: string; changed_at?: string | null } | null
  /** Roadmap R1 U2 — the onboarding declared facts; null until asked. */
  declared_facts?: DeclaredFacts | null
  /** R2 P7 — pointer to the persisted Lectura; the facts come from goalService.getLectura(). */
  lectura_read?: { generated_at: string; kind: 'preliminar' | 'locked'; set_locked_at: { session: string; conversion: string | null }; rules_version: number | null; facts: number; skipped: number; top_fact_id: string | null } | null
  /** Set when the merchant removed the app; passes are paused until a reinstall. */
  uninstalled_at?: string | null
}

export type Postura = 'agil' | 'base' | 'seguro'

/**
 * Switches and markers only (v1.5 rule): a person's answer selects a branch, it is never
 * an operand. Posture today; the priority constraint arrives just in time in R4.
 */
export interface DeclaredFacts {
  postura: Postura
  prioridad?: string
  version?: number
  declared_at?: string
  declared_by?: 'merchant' | 'consultant'
}

export interface DeclaredFactsRead {
  declared_facts: DeclaredFacts | null
  default_risk_level: 'safe' | 'balanced' | 'agile'
  /** Resolved: declared → consultant-set risk level → 'base'. */
  postura: Postura
  postura_source: 'merchant' | 'consultant' | 'declared' | 'risk_level' | 'default'
}

/** R2 push channel: how and when the weekly brief is delivered (one object for email now, WhatsApp later). */
export interface DeliveryPrefsRead {
  channel: 'email' | 'none'
  /** ISO weekday, 1 = lunes … 7 = domingo. */
  weekday: number
  /** Store-local hour, 0..23. */
  hour: number
  tz: string
  primary: string | null
  source: 'default' | 'declared'
  updated_at: string | null
  recipients: { to: string[]; cc: string[]; unsubscribed: string[]; suppressed: Array<{ email: string; reason?: string; at?: string }> }
  last_sent_at: string | null
}

export interface DeliveryPrefsWrite {
  channel?: 'email' | 'none'
  weekday?: number
  hour?: number
  tz?: string
  primary?: string | null
  resubscribe?: string[]
}

export type LifecyclePhase = 'conectar' | 'calibrando' | 'calibrado' | 'plan' | 'iteracion' | 'cierre'

export interface StoreLifecycle {
  phase: LifecyclePhase
  phase_since: string
  lectura: 'none' | 'preliminar' | 'locked'
  locked_reason: 'c_min' | 't_max' | null
  health: {
    tracking: 'ok' | 'stale' | 'missing'
    orders: 'ok' | 'none_yet' | 'missing_webhook' | 'unknown'
    checked_at: string
  }
  evidence: {
    first_session_at: string | null
    last_session_at: string | null
    first_order_at: string | null
    sessions_since_connect: number
    conversions_since_connect: number
    days_since_first_session: number
    closed_through: string | null
  }
  rules_version: number
  set_by: string
  updated_at: string
}

/** A rate with its counts, as the baseline set stores every value. */
export interface BaselineRate { value: number | null; c: number; n: number }

export interface StoreBaselineSet {
  schema: number
  session_rates: {
    locked_at: string
    confidence: 'high' | 'low'
    provenance: { window_start: string; window_end: string; dailies: number; cumulative: boolean; sessions: number; conversions: number }
    values: {
      segment_shares: Record<'bouncers' | 'browsers' | 'shoppers', BaselineRate>
      state_shares: Record<'incoming' | 'browser' | 'shopper' | 'committed' | 'converter', BaselineRate>
      transitions: { engagement: BaselineRate; formation: BaselineRate }
      transitions_by_entry_class: Record<string, { engagement: BaselineRate; formation: BaselineRate }>
      device_shares: Record<string, BaselineRate>
      channel_shares: Record<string, BaselineRate>
      entry_class_shares: Record<string, BaselineRate>
      search_usage_rate: BaselineRate
      classified_coverage?: number
    }
  } | null
  conversion_rates: {
    locked_at: string
    confidence: 'high' | 'low'
    locked_reason: 'c_min' | 't_max'
    values: {
      conversion_rate: BaselineRate
      buyers_share: BaselineRate
      close: BaselineRate
      close_by_entry_class: Record<string, BaselineRate>
      conversion_rate_by_device: Record<string, BaselineRate>
      conversion_rate_by_entry_class: Record<string, BaselineRate>
      conversion_rate_by_channel: Record<string, BaselineRate>
      searcher_conversion_rate: BaselineRate
      aov: { value: number | null; revenue: number; conversions: number }
    }
  } | null
  rules_version?: number | null
  updated_at?: string
}

export interface StorefrontLocation {
  /** URL to open — the canonical one, so no redirect moves us afterwards. */
  url: string
  /**
   * Every origin the window may legitimately end up on (each host plus its
   * www/apex twin). Cross-window messaging must accept any of these: a
   * redirect changes the window's origin, and postMessage then fails silently
   * in both directions — a mismatched targetOrigin is dropped without error.
   */
  origins: string[]
}

/** https origin for a bare host or full URL, plus its www/apex twin. */
function originsOf(raw?: string | null): string[] {
  if (!raw || typeof raw !== 'string') return []
  try {
    const host = new URL(/^https?:\/\//.test(raw) ? raw : `https://${raw}`).hostname
    const twin = host.startsWith('www.') ? host.slice(4) : `www.${host}`
    return [`https://${host}`, `https://${twin}`]
  } catch {
    return []
  }
}

/**
 * Where this store's storefront actually lives.
 *
 * Shared because getting it wrong is invisible: opening `domain` on a
 * custom-domain store still renders the site (via redirect), so the only
 * symptom is that cross-window messaging goes quiet.
 */
export function resolveStorefront(info: StoreInfo | null | undefined, fallback?: string | null): StorefrontLocation | null {
  const canonical =
    info?.url_with_protocol ||
    (Array.isArray(info?.domains) ? info!.domains![0] : undefined) ||
    fallback ||
    info?.domain
  const origins = new Set<string>()
  for (const d of [
    info?.url_with_protocol,
    ...(Array.isArray(info?.domains) ? info!.domains! : []),
    info?.domain,
    info?.original_domain,
    fallback,
  ]) {
    originsOf(d).forEach((o) => origins.add(o))
  }
  if (!canonical || origins.size === 0) return null
  return {
    url: `https://${String(canonical).replace(/^https?:\/\//, '').replace(/\/+$/, '')}`,
    origins: [...origins],
  }
}

export interface ProductsResponse {
  products: any[]
  pagination: { page: number; per_page: number; count: number }
}

export const storeService = {
  async getStore(storeId: string) {
    const res = await apiClient.get<{ success: boolean; data: StoreInfo }>(
      '/api/store',
      { store_id: storeId }
    )
    return res.data
  },

  async getAllStores() {
    const res = await apiClient.get<{ success: boolean; data: StoreInfo[] }>(
      '/api/user/stores'
    )
    return res.data
  },

  async getProducts(storeId: string, page = 1, perPage = 10) {
    const res = await apiClient.get<{ success: boolean; data: ProductsResponse }>(
      '/api/store/products',
      { store_id: storeId, page, per_page: perPage }
    )
    return res.data
  },

  /** GET /api/stores/{id}/declared-facts: the store's switches (posture) and the risk level they map to. */
  async getDeclaredFacts(storeId: string): Promise<DeclaredFactsRead> {
    const res = await apiClient.get<{ success: boolean; data: DeclaredFactsRead }>(
      `/api/stores/${storeId}/declared-facts`
    )
    return res.data
  },

  /** POST /api/stores/{id}/declared-facts (`put_declared_facts`). Posture writes default_risk_level. */
  async putDeclaredFacts(storeId: string, facts: Omit<DeclaredFacts, 'version' | 'declared_at' | 'declared_by'>): Promise<DeclaredFactsRead> {
    const res = await apiClient.post<{ success: boolean; data: DeclaredFactsRead }>(
      `/api/stores/${storeId}/declared-facts`,
      facts
    )
    return res.data
  },

  /** GET /api/stores/{id}/delivery-prefs: when and where the weekly brief goes. */
  async getDeliveryPrefs(storeId: string): Promise<DeliveryPrefsRead> {
    const res = await apiClient.get<{ success: boolean; data: DeliveryPrefsRead }>(
      `/api/stores/${storeId}/delivery-prefs`
    )
    return res.data
  },

  /** POST /api/stores/{id}/delivery-prefs: switches only (channel, day, hour, timezone, recipient). */
  async putDeliveryPrefs(storeId: string, prefs: DeliveryPrefsWrite): Promise<DeliveryPrefsRead> {
    const res = await apiClient.post<{ success: boolean; data: DeliveryPrefsRead }>(
      `/api/stores/${storeId}/delivery-prefs`,
      prefs
    )
    return res.data
  },

  // Get store-level layered analytics
  async getStoreAnalytics(storeId: string, windowDays: number = 7): Promise<LayeredAnalytics> {
    const response: ApiResponse<LayeredAnalytics> = await apiClient.get(
      `/api/store/funnel/analytics`,
      { store_id: storeId, window: windowDays }
    )
    return handleApiResponse(response)
  },
}

